import { VStack } from "@/components/layout/VStack";
import { HStack } from "@/components/layout/HStack";
import { Button } from "@/components/ui/Button/Button";
import { Input } from "@/components/ui/Input/Input";
import { Label } from "@/components/ui/Label/Label";
import { Textarea } from "@/components/ui/Textarea/Textarea";
import {
  SheetBody,
  SheetClose,
  SheetFooter,
} from "@/components/ui/Sheet/Sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/Tooltip/Tooltip";
import { OctagonX } from "lucide-react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useScheduledEmails } from "../../hooks/emails/useScheduledEmails";
import type { ApiScheduledEmail } from "@shared/types/models";

const schema = z.object({
  subject: z
    .string("Subject must be a string")
    .min(1, "Subject is required")
    .max(200, "Subject cannot exceed 200 characters"),
  body: z
    .string("Body must be a string")
    .min(1, "Body is required")
    .max(10000, "Body cannot exceed 10000 characters"),
  date: z
    .string("A date is required")
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date"),
  time: z
    .string("A time is required")
    .regex(/^\d{2}:\d{2}(:\d{2})?$/, "Invalid time"),
});

export type ScheduleEmailFields = z.infer<typeof schema>;

const ErrorIcon = ({ message }: { message?: string }) =>
  message ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <OctagonX
          style={{
            color: "var(--danger)",
            width: "1rem",
            height: "1rem",
            cursor: "default",
          }}
        />
      </TooltipTrigger>
      <TooltipContent>{message}</TooltipContent>
    </Tooltip>
  ) : null;

const ScheduleEmailForm = ({
  onSuccess,
  onCreated,
}: {
  onSuccess: () => void;
  onCreated: (created: ApiScheduledEmail) => void;
}) => {
  const { createScheduledEmail, loading } = useScheduledEmails();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ScheduleEmailFields>({
    resolver: zodResolver(schema),
    defaultValues: {
      subject: "",
      body: "",
      date: "",
      time: "08:00",
    },
  });

  const onSubmit: SubmitHandler<ScheduleEmailFields> = async (data) => {
    const created = await createScheduledEmail(data, onSuccess);
    if (created) onCreated(created);
  };

  return (
    <>
      <SheetBody>
        <form id="schedule-email-form" onSubmit={handleSubmit(onSubmit)}>
          <VStack>
            <VStack gap="2px">
              <HStack gap="0.25rem" align="center">
                <Label>Audience</Label>
              </HStack>
              <Input value="All students" disabled readOnly />
            </VStack>
            <VStack gap="2px">
              <HStack gap="0.25rem" align="center">
                <Label>Subject</Label>
                <ErrorIcon message={errors.subject?.message} />
              </HStack>
              <Input
                {...register("subject")}
                aria-invalid={errors.subject ? true : false}
                placeholder="Subject line"
              />
            </VStack>
            <VStack gap="2px">
              <HStack gap="0.25rem" align="center">
                <Label>Message</Label>
                <ErrorIcon message={errors.body?.message} />
              </HStack>
              <Textarea
                {...register("body")}
                aria-invalid={errors.body ? true : false}
                rows={8}
                placeholder="Plain text — leave a blank line for paragraph breaks. The message will be wrapped in the Friday Explorations email shell."
              />
            </VStack>
            <HStack>
              <VStack gap="2px" style={{ flex: 1 }}>
                <HStack gap="0.25rem" align="center">
                  <Label>Send date</Label>
                  <ErrorIcon message={errors.date?.message} />
                </HStack>
                <Input
                  type="date"
                  {...register("date")}
                  aria-invalid={errors.date ? true : false}
                />
              </VStack>
              <VStack gap="2px" style={{ flex: 1 }}>
                <HStack gap="0.25rem" align="center">
                  <Label>Send time</Label>
                  <ErrorIcon message={errors.time?.message} />
                </HStack>
                <Input
                  type="time"
                  step={1}
                  {...register("time")}
                  aria-invalid={errors.time ? true : false}
                />
              </VStack>
            </HStack>
          </VStack>
        </form>
      </SheetBody>
      <SheetFooter>
        <Button
          type="submit"
          form="schedule-email-form"
          style={{ width: "100%" }}
          disabled={isSubmitting || loading.create}
        >
          {isSubmitting || loading.create ? "Loading..." : "Schedule Email"}
        </Button>
        <SheetClose asChild>
          <Button
            variant="outline"
            disabled={isSubmitting}
            style={{ width: "100%" }}
          >
            Cancel
          </Button>
        </SheetClose>
      </SheetFooter>
    </>
  );
};

export { ScheduleEmailForm };
